import type { ReportTypeId } from '../../config/reportTypes';
import {
  resolveGenerationTier,
  type GenerationTier,
} from '../../config/generationTier';

/**
 * User-facing runtime estimate stamped onto every generation job.
 *
 * This is deliberately independent of the progress percentage: the worker
 * emits synthetic progress pulses while long model/review calls are running,
 * so extrapolating from percent would imply precision the pipeline does not
 * have. Narrative-heavy production runs have been observed around 55 minutes.
 */
export const REPORT_GENERATION_ETA_BASIS = 'report-type-baseline-2026-09-v1' as const;

const MINUTE_MS = 60_000;
const DEFAULT_INVESTMENT_GRADE_DURATION_MS = 55 * MINUTE_MS;
const DEFAULT_DRAFT_DURATION_MS = 30 * MINUTE_MS;

const INVESTMENT_GRADE_DURATION_BY_TYPE: Partial<Record<ReportTypeId, number>> = {
  'custom-brief': 40 * MINUTE_MS,
  'test-fit': 40 * MINUTE_MS,
};

export function estimatedReportGenerationDurationMs(
  reportTypeId: ReportTypeId,
  generationTier?: GenerationTier,
): number {
  // `draft` survives at persisted/request boundaries for backward
  // compatibility, but the generation pipeline currently canonicalizes it to
  // investment grade. Resolve it here too so an old queued request never gets
  // a 30-minute promise for the full-quality ~55-minute path.
  if (resolveGenerationTier(generationTier) === 'draft') {
    return DEFAULT_DRAFT_DURATION_MS;
  }
  return INVESTMENT_GRADE_DURATION_BY_TYPE[reportTypeId]
    ?? DEFAULT_INVESTMENT_GRADE_DURATION_MS;
}

export interface GenerationCountdownPresentation {
  label: string;
  ariaLabel: string;
  overdue: boolean;
}

/**
 * Resolve one stable enqueue-time anchor across live-job and operation-only
 * states. An operation's `refreshStartedAt` may survive from a previous run,
 * while every fresh queue request replaces `refreshRequestedAt`.
 */
export function resolveGenerationCountdownStartedAt(input: {
  runAcceptedAt?: string | null;
  refreshRequestedAt?: string | null;
  refreshStartedAt?: string | null;
}): string | null {
  return input.runAcceptedAt
    ?? input.refreshRequestedAt
    ?? input.refreshStartedAt
    ?? null;
}

function minutesLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder > 0 ? `${hours} hr ${remainder} min` : `${hours} hr`;
}

/** Pure formatter used by the live badge and fake-clock tests. */
export function generationCountdownPresentation(input: {
  startedAt?: string | null;
  estimatedDurationMs?: number | null;
  nowMs?: number;
}): GenerationCountdownPresentation | null {
  const startedAtMs = Date.parse(input.startedAt ?? '');
  const durationMs = input.estimatedDurationMs ?? Number.NaN;
  const nowMs = input.nowMs ?? Date.now();
  if (
    !Number.isFinite(startedAtMs)
    || !Number.isFinite(durationMs)
    || !Number.isFinite(nowMs)
    || durationMs <= 0
  ) return null;
  // A materially future start usually means clock skew or corrupt persisted
  // data. Falling back to the lifecycle label is more honest than counting
  // down from a run that has not begun.
  if (startedAtMs - nowMs > MINUTE_MS) return null;

  const elapsedMs = Math.max(0, nowMs - startedAtMs);
  const remainingMs = durationMs - elapsedMs;
  if (remainingMs > 0) {
    const remainingMinutes = Math.max(1, Math.ceil(remainingMs / MINUTE_MS));
    const duration = minutesLabel(remainingMinutes);
    return {
      label: `~${duration} remaining`,
      ariaLabel: `Analysis running, about ${duration} remaining`,
      overdue: false,
    };
  }

  const elapsedMinutes = Math.max(1, Math.floor(elapsedMs / MINUTE_MS));
  const elapsed = minutesLabel(elapsedMinutes);
  return {
    label: `Finishing up · ${elapsed} elapsed`,
    ariaLabel: `Analysis is finishing up after ${elapsed} elapsed`,
    overdue: true,
  };
}
